import { useEffect, useRef } from 'react';
import { clarityEvent, gaEvent } from '../analytics';

/**
 * @typedef {Object} SectionViewOptions
 * @property {number} [threshold]
 * @property {() => void} [onView]
 */

/**
 * Tracks one-time section visibility per page load.
 *
 * @param {string} sectionName
 * @param {SectionViewOptions} [options]
 * @returns {{ current: HTMLElement | null }}
 */
export default function useSectionViewTracking(sectionName, options = {}) {
  const sectionRef = useRef(null);
  const hasTrackedRef = useRef(false);
  const threshold = options.threshold ?? 0.5;
  const onViewRef = useRef(options.onView);

  useEffect(() => {
    onViewRef.current = options.onView;
  }, [options.onView]);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || hasTrackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (hasTrackedRef.current) return;
          if (!entry.isIntersecting) return;
          if (entry.intersectionRatio < threshold) return;

          hasTrackedRef.current = true;
          gaEvent('section_view', { section_name: sectionName });
          clarityEvent('section_view');

          if (typeof onViewRef.current === 'function') {
            onViewRef.current();
          }

          observer.disconnect();
        });
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [sectionName, threshold]);

  return sectionRef;
}
